/*
 * ENROLLING THIS DEVICE, AS A HOOK: the mechanism, shared by the two surfaces
 * that offer it.
 *
 * WHY THIS EXISTS RATHER THAN A SECOND COPY, and the precedent is in the
 * onboarding flow already. The fencer search is ONE component shared between
 * onboarding and manage-fencers because the cap arithmetic would otherwise
 * drift, and the copy that drifted would be the onboarding one nobody looks at
 * again after their first week. The same argument applies exactly here: the
 * four conditions under which a device cannot be enrolled, the worker
 * registration that has to happen before the tap, and the shape of the
 * `enablePush` call are all rules rather than layout, and two implementations
 * of them would answer differently within a release.
 *
 * WHAT IS SHARED IS THE MECHANISM AND NOT THE MARKUP, which is the other half
 * of that precedent. `ManageNotifications`' `DeviceControl` renders four states
 * as four different sentences, because a reader who navigated to a page about
 * notifications is owed an answer even when the answer is "not on this device".
 * The onboarding offer renders none of them: a reader trying to finish setting
 * the app up is owed a SHORTER FLOW, so every one of those conditions is a
 * skipped step there. Same rules, different shapes, and this file is the rules.
 *
 * NOTHING HERE PROMPTS. `enable` must be called from inside a gesture handler
 * and it calls `enablePush` synchronously so the gesture survives (see
 * lib/push.ts's header for why that is a platform requirement and not only
 * Kevin's ruling). The mount effect registers the service worker and reads the
 * browser's own subscription, neither of which asks the reader anything.
 */

import { useCallback, useEffect, useState } from "react";

import { api } from "./api";
import {
  currentSubscription,
  disablePush,
  enablePush,
  installedApp,
  prepareRegistration,
  pushBlocked,
  pushSupported,
  syncSubscription,
  type EnableResult,
} from "./push";

/** The outcomes a screen says something about; `enabled` and `unsupported` are states instead. */
export type EnrolmentResult = "denied" | "dismissed" | "failed" | null;

export interface PushEnrolment {
  /**
   * Whether this browser AND this build can enrol a device at all — push
   * support plus a VAPID key on /api/me. Synchronous, because both callers need
   * it before they render anything.
   */
  supported: boolean;

  /**
   * Whether the permission has been permanently refused. Read live rather than
   * captured, because it can change under us: a reader can answer the browser's
   * prompt while this component is mounted.
   */
  blocked: boolean;

  /**
   * Whether this device already holds a subscription — `undefined` while the
   * probe is still in flight, which is a THIRD answer rather than a slow
   * `false`. The onboarding offer treats it as "do not offer": the failure
   * direction there is a skipped step the reader can still find in Settings,
   * where the other direction is an offer to enrol a device that already is.
   */
  enrolled: boolean | undefined;

  /** A call is in flight. Both callers disable their controls on it. */
  busy: boolean;

  /** The last outcome worth telling the reader about, or null. */
  result: EnrolmentResult;

  /**
   * Whether the app is running installed rather than in a browser tab, which
   * decides where a blocked permission is undone. Exposed here so neither
   * screen reads the globals itself.
   */
  installed: boolean;

  /**
   * Ask for permission and enrol. MUST be called from a gesture handler.
   *
   * Returns the outcome as well as recording it, because the two callers do
   * different things with it: the settings control re-renders, and the
   * onboarding step decides whether to move on.
   */
  enable: () => Promise<EnableResult>;

  /** Unenrol this device, both sides. */
  disable: () => void;
}

export interface PushEnrolmentOptions {
  /**
   * Whether to RE-ASSERT a subscription this browser already holds (see
   * `syncSubscription`). True on the notifications page, which is what makes
   * the two sides self-healing without a read endpoint.
   *
   * FALSE DURING ONBOARDING, deliberately. Onboarding's job is to make an
   * OFFER, and repairing a server row is a different job belonging to the page
   * that manages the enrolment. "Not now" has to leave nothing behind, and a
   * silent write on the way past is something.
   */
  sync: boolean;
}

export function usePushEnrolment(
  publicKey: string,
  { sync }: PushEnrolmentOptions,
): PushEnrolment {
  const [enrolled, setEnrolled] = useState<boolean | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<EnrolmentResult>(null);

  const supported = pushSupported() && publicKey !== "";

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;

    // REGISTER THE WORKER NOW, so it is ACTIVE by the time the button is
    // pressed. `pushManager.subscribe` needs an active worker, and a worker
    // registered inside the click handler is still installing when the call
    // lands — which is what made the first tap on a fresh install report "Could
    // not save that" before succeeding on the second (iOS, 2026-09-12).
    // lib/push.ts waits for activation either way; this is what keeps that wait
    // at zero.
    //
    // It asks the reader nothing, so it is not what "never prompt on load"
    // forbids: that rule is about Notification.requestPermission, which still
    // has exactly one call path and it is `enable` below.
    void prepareRegistration();

    void currentSubscription().then((sub) => {
      if (cancelled) return;
      setEnrolled(sub !== null);
    });

    if (sync) void syncSubscription(api.subscribePush);

    return () => {
      cancelled = true;
    };
  }, [supported, sync]);

  const enable = useCallback(async () => {
    setBusy(true);
    setResult(null);
    try {
      // Called synchronously inside the caller's gesture handler: `enablePush`
      // takes the key as an argument precisely so there is no round trip
      // between the tap and the prompt.
      const outcome = await enablePush(publicKey, api.subscribePush);
      setEnrolled(outcome === "enabled");
      setResult(outcome === "enabled" || outcome === "unsupported" ? null : outcome);
      return outcome;
    } finally {
      setBusy(false);
    }
  }, [publicKey]);

  const disable = useCallback(() => {
    setBusy(true);
    setResult(null);
    void disablePush(api.unsubscribePush)
      .then(() => setEnrolled(false))
      .finally(() => setBusy(false));
  }, []);

  return {
    supported,
    blocked: pushBlocked(),
    enrolled,
    busy,
    result,
    installed: installedApp(),
    enable,
    disable,
  };
}

/**
 * Whether a FIRST-RUN offer is worth making on this device.
 *
 * THE FOUR REASONS TO SHOW NOTHING ARE FOUR REASONS AND ONE OUTCOME. An
 * unsupported browser, a build with no VAPID key, a permission already refused
 * and a device already enrolled are different facts about different things —
 * but an offer answers all four the same way, by not being made. Skipping a
 * step is not the same act as rendering an explanation, and this is the
 * predicate that says which one an offer does.
 *
 * The settings page deliberately does NOT use this: there, each of the four is
 * its own sentence, because a page about notifications that rendered nothing
 * would be a bug rather than a shorter flow.
 */
export function offerable(enrolment: PushEnrolment): boolean {
  return enrolment.supported && !enrolment.blocked && enrolment.enrolled === false;
}
